import React from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@apollo/client';
import { useModal } from 'hooks/useModal';
import { ModalsKeys, QueryResponse } from 'global';
import { Cancel, Save } from '@mui/icons-material';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { InputField, ColorPickerField } from 'components/form';
import { Button, CircularProgress, Stack } from '@mui/material';
import { CalendarViewConfigurationViewModel } from '@trnsact/trnsact-shared-types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { CalendarMapper } from '../../mapper';
import { calendarApi, useCalendar } from '../../api';
import { DealActivityAPINameSpace } from '../../../api/types';
import { UpdateCalendarViewFormValues, updateCalendarViewFormSchema } from './updateCalendarViewFormSchema';

export const UpdateCalendarViewDialog = () => {
  const { handleUpdateCalendarView, isUpdatingCalendarView } = useCalendar();
  const { data: modalData, isOpen, handleClose } = useModal(ModalsKeys.UpdateCalendarViewDialog);

  const {
    error,
    loading,
    data: configData,
  } = useQuery<DealActivityAPINameSpace<QueryResponse<CalendarViewConfigurationViewModel>>>(
    calendarApi.sharing.queries.configurationItem,
    {
      skip: !isOpen || !modalData.id,
      variables: {
        input: {
          id: modalData.id,
        },
      },
      onCompleted(data) {
        const configItem = data?.dealActivity?.result?.entity;

        if (!configItem) return;

        reset({
          name: configItem.name ?? '',
          color: configItem.color ?? '',
        });
      },
    }
  );

  const configItem = configData?.dealActivity?.result?.entity;

  const { control, handleSubmit, reset, setValue } = useForm<UpdateCalendarViewFormValues>({
    resolver: zodResolver(updateCalendarViewFormSchema),
    defaultValues: { name: '', color: '' },
  });

  const onSubmit = async (formValues: UpdateCalendarViewFormValues) => {
    await handleUpdateCalendarView(CalendarMapper.mapFormDtoToUpdateView(modalData.id, formValues), api => ({
      awaitRefetchQueries: true,
      refetchQueries: [api.sharing.queries.configuration, api.events.queries.listWithActivities],
    }));

    handleClose();
  };

  return (
    <CustomDialog
      isOpen={isOpen}
      onClose={handleClose}
      label="Update Calendar View"
      actions={
        <Stack direction="row" gap="1rem" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSubmit(onSubmit)}
            loading={isUpdatingCalendarView}
          >
            Save
          </Button>

          <Button onClick={handleClose} startIcon={<Cancel />} disabled={isUpdatingCalendarView}>
            Cancel
          </Button>
        </Stack>
      }
    >
      <RenderWithFallback
        error={error}
        isLoading={loading}
        fallBack={
          <Stack alignItems="center" justifyContent="center" height="100%">
            <CircularProgress />
          </Stack>
        }
      >
        <Stack gap="1rem" direction="row">
          <Stack width="2rem" alignItems="center" justifyContent="center">
            <ColorPickerField initialColor={configItem?.color} onColorChanged={value => setValue('color', value)} />
          </Stack>

          <InputField
            name="name"
            label="Name"
            control={control}
            inputProps={{ fullWidth: true, variant: 'outlined', required: true }}
          />
        </Stack>
      </RenderWithFallback>
    </CustomDialog>
  );
};
